import React, { useContext, useEffect, useState } from 'react';
import { useAppSelector } from '../../store/configureStore';
import { useSnackbar } from 'notistack';
import {
  Button,
  IconButton,
  DialogTitle,
  Grid,
  Typography,
  Dialog,
  DialogContent
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ApplicationContext } from '../../context/Contexts';
import {
  AddInvoiceCaseAttachmentRequestDto,
  AddInvoiceCaseAttachmentResponseDto
} from '../../interfaces/Case';
import { OrganizationInfoDto } from '../../interfaces/Organization';
import FileUploadRow from './FileUploadRow';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import NumberFormat from 'react-number-format';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ScaleLoader } from 'react-spinners';
import { Utils } from '../../helpers/Utils';

const NumberFormatCustom = (numberProps: any) => {
  const { inputRef, onChange, ...other } = numberProps;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values: any) => {
        onChange({ target: { value: values.value } });
      }}
      allowNegative={false}
      decimalScale={0}
      isNumericString
    />
  );
};

const NumberFormatCustomDecimal = (numberProps: any) => {
  const { inputRef, onChange, ...other } = numberProps;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values: any) => {
        onChange({
          target: { value: values.value },
          currentTarget: { value: values.value }
        });
      }}
      allowNegative={false}
      thousandSeparator={false}
      decimalScale={2}
    />
  );
};

export interface RepairServiceInvoiceModalProps {
  open: boolean;
  caseId: number;
  serviceOrganization: OrganizationInfoDto | null;
  attachmentTypeId: number;
  attachmentTypeCode: string;
  vatValue: number | null;
  currency: string;
  onClose: () => void;
  onSaved: (response: AddInvoiceCaseAttachmentResponseDto) => void | Promise<void>;
}

const RepairServiceInvoiceModal = (props: RepairServiceInvoiceModalProps) => {
  const { open, serviceOrganization, onClose, onSaved } = props;
  const { translatorService, caseService } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();
  const appState = useAppSelector((state) => state.app);

  const buildInitialInvoice = (): AddInvoiceCaseAttachmentRequestDto => ({
    caseId: props.caseId,
    attachmentTypeId: props.attachmentTypeId,
    attachmentTypeCode: props.attachmentTypeCode,
    invoiceNumber: '',
    invoiceDate: '',
    amount: 0,
    amountWithoutVat: 0,
    vatValue: props.vatValue as number,
    vatAmount: 0,
    issuerOrganizationId: serviceOrganization?.id ?? 0,
    currency: props.currency,
    file: null
  });

  const [invoice, setInvoice] = useState<AddInvoiceCaseAttachmentRequestDto>(buildInitialInvoice());
  const [isLoading, setIsLoading] = useState(false);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (open) {
      setInvoice(buildInitialInvoice());
      setDateError('');
    }
  }, [open]);

  const computeVatAmount = (amountWithoutVat: number, vatPercent: number): number =>
    Utils.Round((amountWithoutVat * vatPercent) / 100);

  const handleInvoiceNumberChange = (value: string) => {
    setInvoice((prev) => ({ ...prev, invoiceNumber: value }));
  };

  const handleDateChange = (date: MaterialUiPickersDate | null) => {
    if (date === null || !date.isValid()) {
      return;
    }
    setDateError('');
    setInvoice((prev) => ({ ...prev, invoiceDate: date.format('YYYY-MM-DD') }));
  };

  const validateDate = () => {
    setDateError(
      invoice.invoiceDate
        ? ''
        : translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
    );
  };

  const handleAmountWithoutVatChange = (value: string) => {
    const amountWithoutVat = value === '' ? 0 : parseFloat(value);
    setInvoice((prev) => {
      const vatAmount = computeVatAmount(amountWithoutVat, prev.vatValue ?? 0);
      return {
        ...prev,
        amountWithoutVat: amountWithoutVat,
        vatAmount: vatAmount,
        amount: Utils.Round(amountWithoutVat + vatAmount)
      };
    });
  };

  const handleVatPercentChange = (value: string) => {
    const vatValue = value === '' ? (null as any) : parseFloat(value);
    setInvoice((prev) => {
      const vatAmount = computeVatAmount(prev.amountWithoutVat, vatValue ?? 0);
      return {
        ...prev,
        vatValue: vatValue,
        vatAmount: vatAmount,
        amount: Utils.Round(prev.amountWithoutVat + vatAmount)
      };
    });
  };

  const save = async () => {
    if (!invoice.file) {
      enqueueSnackbar(
        translatorService.Tranlate('FILE_REQUIRED', 'Va rugam selectati un fisier.'),
        { variant: 'warning' }
      );
      return;
    }
    if (!invoice.invoiceDate) {
      validateDate();
      return;
    }

    setIsLoading(true);
    try {
      const response = await caseService.AddInvoiceCaseAttachment(invoice);
      await onSaved(response);
      onClose();
    } catch {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  const currencySuffix = invoice.currency ? ` (${invoice.currency})` : '';

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle id="repair-service-invoice-title">
        <Grid container>
          <Grid item xs={11}>
            <Typography variant="h4">
              {translatorService.Tranlate(
                'REPAIR_SERVICE_INVOICE_MODAL_TITLE',
                'Adauga factura service reparator'
              )}
            </Typography>
          </Grid>
          <Grid className="text-right" item xs={1}>
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <ValidatorForm onSubmit={save}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                id="repairServiceInvoiceService"
                className="m-2"
                options={serviceOrganization ? [serviceOrganization] : []}
                getOptionLabel={(o) => o.name ?? ''}
                value={serviceOrganization ?? null}
                disabled
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="serviceName"
                    value={serviceOrganization?.name ?? ''}
                    label={translatorService.Tranlate(
                      'REPAIR_SERVICE_INVOICE_SERVICE_NAME',
                      'Nume service'
                    )}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                className="m-2"
                name="invoiceNumber"
                value={invoice.invoiceNumber}
                onChange={(e: any) => handleInvoiceNumberChange(e.target.value)}
                label={translatorService.Tranlate('REPAIR_SERVICE_INVOICE_NUMBER', 'Numar factura')}
                validators={['required']}
                errorMessages={[
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MuiPickersUtilsProvider
                libInstance={moment}
                utils={MomentUtils}
                locale={appState.language}
              >
                <DatePicker
                  fullWidth
                  className="m-2"
                  variant="inline"
                  openTo="date"
                  views={['year', 'month', 'date']}
                  format={appState.dateFormat.toUpperCase()}
                  margin="normal"
                  id="invoiceDate"
                  label={translatorService.Tranlate('REPAIR_SERVICE_INVOICE_DATE', 'Data factura')}
                  value={invoice.invoiceDate || null}
                  onChange={handleDateChange}
                  autoOk={true}
                  required={true}
                  error={dateError ? true : false}
                  helperText={dateError}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                className="m-2"
                name="amountWithoutVat"
                value={invoice.amountWithoutVat || ''}
                onChange={(e: any) => handleAmountWithoutVatChange(e.target.value)}
                label={
                  translatorService.Tranlate(
                    'REPAIR_SERVICE_INVOICE_AMOUNT_WITHOUT_VAT',
                    'Valoare fara TVA'
                  ) + currencySuffix
                }
                validators={['required', 'minNumber:1']}
                errorMessages={[
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
                ]}
                InputProps={{
                  inputComponent: NumberFormatCustomDecimal,
                  style: { fontWeight: 500 }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                className="m-2"
                name="vatValue"
                value={invoice.vatValue}
                onChange={(e: any) => handleVatPercentChange(e.target.value)}
                label={translatorService.Tranlate('REPAIR_SERVICE_INVOICE_VAT_PERCENT', 'TVA (%)')}
                validators={['required', 'minNumber:0']}
                errorMessages={[
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
                ]}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  style: { fontWeight: 500 }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                className="m-2"
                name="vatAmount"
                value={invoice.vatAmount || ''}
                disabled
                label={
                  translatorService.Tranlate('REPAIR_SERVICE_INVOICE_VAT_AMOUNT', 'Valoare TVA') +
                  currencySuffix
                }
                InputProps={{
                  inputComponent: NumberFormatCustomDecimal,
                  style: { fontWeight: 500 }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                className="m-2"
                name="totalWithVat"
                value={invoice.amount || ''}
                disabled
                label={
                  translatorService.Tranlate('REPAIR_SERVICE_INVOICE_TOTAL_WITH_VAT', 'Total cu TVA') +
                  currencySuffix
                }
                InputProps={{
                  inputComponent: NumberFormatCustomDecimal,
                  style: { fontWeight: 500 }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FileUploadRow
                inputId="repair-service-invoice-file"
                accept="application/pdf"
                title={translatorService.Tranlate(
                  'REPAIR_SERVICE_INVOICE_ADD_FILE',
                  'Adauga fisier factura'
                )}
                emptyDescription={translatorService.Tranlate(
                  'REPAIR_SERVICE_INVOICE_SELECT_FILE_DESC',
                  'Selecteaza un fisier .pdf'
                )}
                uploadButtonLabel={translatorService.Tranlate(
                  'REPAIR_SERVICE_INVOICE_UPLOAD_FILE',
                  'Incarca fisier'
                )}
                fileName={invoice.file?.name ?? null}
                onFileSelected={(file) => setInvoice((prev) => ({ ...prev, file }))}
                onFileRemoved={() => setInvoice((prev) => ({ ...prev, file: null }))}
              />
            </Grid>
          </Grid>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={isLoading} />
          </div>
          <div className="text-right">
            <Button
              className="m-2"
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isLoading}
              onClick={() => {
                validateDate();
              }}
            >
              {translatorService.Tranlate('SAVE', 'Salveaza')}
            </Button>
          </div>
        </ValidatorForm>
      </DialogContent>
    </Dialog>
  );
};

export default RepairServiceInvoiceModal;
